import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { Row, Col } from 'react-bootstrap';
import * as uuidLib from 'uuid';

import { getLogger, intlModule, communicationVideoModule as videoComms, communicationModule } from '@chedri/base';

import { getChatterUserAvatarInfos } from 'helpers/clients';

import { mayUseAudioCall, mayUseVideoCall } from 'helpers/webrtccall';
import Avatar from '../Avatar';
import IconButton from '../IconButton';

const logger = getLogger('communication');

function UserInfos({ me, contact }) {
  const dispatch = useDispatch();

  const messages = useSelector(intlModule.selectors.getMessages);
  const hasPendingCalls = useSelector(videoComms.selectors.hasPendingCalls);
  const isSocketConnected = useSelector(communicationModule.selectors.socket.isConnected);
  const isNetworkOnline = useSelector(state => state.webApp.isOnline);

  const [canDoAudioCall, setCanDoAudioCall] = useState(false);
  const [canDoVideoCall, setCanDoVideoCall] = useState(false);
  const {
    first_name: firstName,
    last_name: lastName,
    avatarThumbLink: userThumbLink,
    avatar,
    is_online: isOnline,
    is_mobile_online: isMobileOnline,
    is_audio_call_available: isAudioCallAvailable,
    is_video_call_available: isVideoCallAvailable,
    role: contactRole,
  } = contact || {};
  const { avatarThumbLink, avatarTextColor, avatarColor } =
    contact != null
      ? getChatterUserAvatarInfos({
          name: contact.name || firstName + lastName,
          avatar: typeof avatar === 'string' ? avatar : userThumbLink,
        })
      : {};

  const startCall = useCallback(
    type => {
      if (!contact) {
        return;
      }
      const uuid = uuidLib.v4();
      navigator.mediaDevices
        .getUserMedia({
          audio: true,
        })
        .then(stream => {
          stream.getTracks().forEach(track => {
            track.stop();
          });

          dispatch(
            videoComms.actions.startCall({
              type,
              from: { id: me.username, name: me.name },
              to: { id: contact.id, name: `${contact.first_name} ${contact.last_name}` },
              uuid,
            })
          );
          dispatch(videoComms.actions.updateCallId(uuid, contact.id));
        })
        .catch(err => console.error('Error initiating call', err));
    },
    [contact]
  );

  const checkAudioAvailability = useCallback(async () => {
    try {
      const audioAvailable = await mayUseAudioCall();
      if (audioAvailable && isAudioCallAvailable) {
        logger.debug('Audio call is available to', contact);
        setCanDoAudioCall(true);
      } else {
        logger.debug('Audio call is not available to', contact);
        setCanDoAudioCall(false);
      }
    } catch (err) {
      console.error('Error calculating audio call usage', err);
      setCanDoAudioCall(false);
    }
  }, [isAudioCallAvailable, setCanDoAudioCall]);

  const checkVideoAvailability = useCallback(async () => {
    try {
      const videoAvailable = await mayUseVideoCall();
      if (videoAvailable && isVideoCallAvailable) {
        logger.debug('Video call is available to', contact);
        setCanDoVideoCall(true);
      } else {
        logger.debug('Video call is not available to', contact);
        setCanDoVideoCall(false);
      }
    } catch (err) {
      console.error('Error calculating video call usage', err);
      setCanDoVideoCall(false);
    }
  }, [isVideoCallAvailable, setCanDoVideoCall]);

  useEffect(() => {
    checkAudioAvailability();
    checkVideoAvailability();
  }, [checkAudioAvailability, checkVideoAvailability]);

  return (
    <div>
      <Row>
        <Col xs={12}>
          <div style={{ margin: '5em' }}>
            <Avatar
              firstName={firstName}
              lastName={lastName}
              textColor={avatarTextColor}
              color={avatarColor}
              thumbLink={avatarThumbLink}
            />
          </div>
          <div className="text-center m-b-20">
            <h3>
              {firstName} {lastName}
            </h3>
          </div>
          {contactRole !== 'customer' && !hasPendingCalls && (
            <div className="text-center">
              <IconButton
                name="fa-phone"
                duotone
                buttonClassName="btn btn-success btn-trp m-r-5"
                onClick={() => startCall('audio')}
                disabled={!((isOnline || isMobileOnline) && canDoAudioCall && isSocketConnected && isNetworkOnline)}
                tooltip={messages.startAudioCallWith.replace(/\{\{(to)\}\}/gi, `${firstName} ${lastName}`)}
                tooltipPlacement="bottom"
              />
              <IconButton
                name="fa-video"
                duotone
                buttonClassName="btn btn-success btn-trp"
                onClick={() => startCall('video')}
                disabled={!((isOnline || isMobileOnline) && canDoVideoCall && isSocketConnected && isNetworkOnline)}
                tooltip={messages.startVideoCallWith.replace(/\{\{(to)\}\}/gi, `${firstName} ${lastName}`)}
                tooltipPlacement="bottom"
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}
UserInfos.defaultProps = {};
UserInfos.propTypes = {
  me: PropTypes.object.isRequired,
  contact: PropTypes.object.isRequired,
};

export default UserInfos;
